import type { BadgeTone } from '../components/ui';

// ─────────────────────────────────────────────────────────────────
// D5 · Äldre ansökningar: "Ansök till marken" (mark_applications) och ansökan till jaktklubb
// (jaktklubb_applications). Nya ansökningar görs via markens annonser; de här visas skrivskyddade
// tills de är avgjorda. Godkänn har samma effekt som förut, avslå stänger ansökan.
// ─────────────────────────────────────────────────────────────────

export type LegacyKind = 'mark' | 'jaktklubb';

export interface LegacyApplication {
  id: string;
  kind: LegacyKind;
  name: string;
  email: string;
  /** Vad ansökan gäller: marken eller jaktklubbens grupp. */
  target: string;
  text: string | null;
  status: string;
  /** Millisekunder. */
  createdAt: number;
}

const LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Väntar på beslut', tone: 'accent' },
  interest_registered: { label: 'Intresse anmält', tone: 'accent' },
  question_asked: { label: 'Fråga till jägaren', tone: 'info' },
  dispensation_requested: { label: 'Dispens begärd', tone: 'info' },
  dispensation_granted: { label: 'Dispens beviljad', tone: 'info' },
  dispensation_denied: { label: 'Dispens nekad', tone: 'danger' },
  contract_sent: { label: 'Avtal skickat', tone: 'info' },
  user_signed: { label: 'Väntar på markägarens signatur', tone: 'info' },
  contract_signed: { label: 'Avtal signerat', tone: 'ok' },
  approved: { label: 'Godkänd', tone: 'ok' },
  qualified: { label: 'Kvalificerad', tone: 'ok' },
  completed: { label: 'Klar – medlem', tone: 'ok' },
  active: { label: 'Klar – medlem', tone: 'ok' },
  rejected: { label: 'Avslagen', tone: 'danger' },
};

export function legacyStatus(status: string): { label: string; tone: BadgeTone } {
  return LABELS[status] ?? { label: status, tone: 'neutral' };
}

/** Ansökningar som ingen beslutat om ännu – markägaren kan godkänna eller avslå dem. */
const UNDECIDED = ['pending', 'interest_registered', 'question_asked', 'dispensation_requested', 'dispensation_granted'];
export const isUndecided = (status: string) => UNDECIDED.includes(status);

/** Avgjorda (godkända, avslagna, klara) visas bara på begäran. Avtal på väg räknas som öppna. */
const CLOSED = ['approved', 'qualified', 'completed', 'active', 'rejected', 'dispensation_denied'];
export const isClosed = (status: string) => CLOSED.includes(status);

/** Öppna först, nyaste först inom varje del. */
export function sortLegacy(list: LegacyApplication[]): LegacyApplication[] {
  return [...list].sort((a, b) => Number(isClosed(a.status)) - Number(isClosed(b.status)) || b.createdAt - a.createdAt);
}

/** API:t ger ISO-tid för markansökningar och millisekunder för jaktklubbsansökningar. */
export function toMs(v: string | null | undefined): number {
  if (!v) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : Date.parse(v) || 0;
}
